import { Component, useId } from 'react';
import type { ReactNode } from 'react';

/**
 * BillingCard — the consistent shell every card on the Billing tab uses.
 *
 * Owns the four cross-cutting concerns so the cards themselves stay declarative:
 *   - header (title + optional right-side slot for a badge / toggle / action)
 *   - loading  → skeleton sized to the card
 *   - error    → message + Retry button
 *   - render error → contained by a per-card error boundary (one broken card
 *     can't blank the whole tab)
 */

interface BillingCardProps {
  title: string;
  /** Right-aligned header slot — a tier badge, expand toggle, etc. */
  headerRight?: ReactNode;
  loading?: boolean;
  error?: string | null;
  onRetry?(): void;
  /** Skeleton line count while loading. */
  skeletonRows?: number;
  /** Visually flag a destructive ("danger zone") card. */
  tone?: 'default' | 'danger';
  children?: ReactNode;
}

export function BillingCard({
  title,
  headerRight,
  loading = false,
  error = null,
  onRetry,
  skeletonRows = 3,
  tone = 'default',
  children,
}: BillingCardProps) {
  const titleId = useId();
  return (
    <section
      className={`billing-card${tone === 'danger' ? ' billing-card--danger' : ''}`}
      aria-labelledby={titleId}
      aria-busy={loading || undefined}
    >
      <header className="billing-card__head">
        <h3 className="billing-card__title" id={titleId}>
          {title}
        </h3>
        {headerRight != null && <div className="billing-card__head-right">{headerRight}</div>}
      </header>

      <div className="billing-card__body">
        {loading ? (
          <CardSkeleton rows={skeletonRows} />
        ) : error ? (
          <CardError message={error} onRetry={onRetry} />
        ) : (
          <CardErrorBoundary>{children}</CardErrorBoundary>
        )}
      </div>
    </section>
  );
}

/** Pulsing placeholder lines that match the card's text rhythm. */
export function CardSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="billing-skeleton" aria-hidden="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="billing-skeleton__line"
          style={{ width: `${[90, 70, 80, 60, 75][i % 5]}%` }}
        />
      ))}
    </div>
  );
}

function CardError({ message, onRetry }: { message: string; onRetry?: (() => void) | undefined }) {
  return (
    <div className="billing-inline-error" role="alert">
      <span>{message}</span>
      {onRetry && (
        <button type="button" className="billing-link-btn" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

/** Per-card error boundary. A render error in one card shows a small fallback
 *  instead of taking down the whole Settings modal. */
class CardErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="billing-inline-error" role="alert">
          <span>Something went wrong displaying this section.</span>
          <button
            type="button"
            className="billing-link-btn"
            onClick={() => this.setState({ failed: false })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
